import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { api, errorMessage } from "../../lib/api";
import { ROLES } from "../../data/content";
import { Field, TextInput, Choice, Honeypot, FormStatus } from "./Fields";

const EMPTY = { name: "", email: "", role: "", follow_up: false, website: "" };

export const DownloadDialog = ({ resource, trigger, idPrefix = "dl" }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [url, setUrl] = useState("");
  const id = (k) => `${idPrefix}-${resource.id.slice(0, 6)}-${k}`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setStatus(null);
    try {
      const { data } = await api.post(`/resources/${resource.id}/request`, f);
      const full = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      setUrl(full);
      setStatus({ type: "success", title: "Your download is starting", text: f.follow_up ? "Thank you! Michelle will be in touch by email." : "Thank you for your interest." });
      toast.success("Download started");
      window.location.assign(full);
    } catch (err) {
      setStatus({ type: "error", title: "Download didn't start", text: errorMessage(err) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setStatus(null); setUrl(""); } }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-cream text-ink max-w-lg rounded-2xl" data-testid="download-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">Get the free {resource.title}</DialogTitle>
          <DialogDescription className="text-slate">Enter your name and email and the download starts right away.</DialogDescription>
        </DialogHeader>
        {url ? (
          <div className="space-y-5 font-sans">
            <FormStatus status={status} testid="download-status" />
            <a href={url} className="inline-flex items-center gap-2 text-teal underline underline-offset-4" data-testid="download-again-link"><Download className="w-4 h-4" aria-hidden="true" /> Download didn't start? Click here</a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 font-sans" aria-label={`Download ${resource.title}`} data-testid="download-form">
            <Field id={id("name")} label="Name" required><TextInput id={id("name")} autoComplete="name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field id={id("email")} label="Email" required><TextInput id={id("email")} type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field id={id("role")} label="I am a"><Choice id={id("role")} value={f.role} onChange={(v) => setF({ ...f, role: v })} options={ROLES} /></Field>
            <div className="flex items-start gap-3">
              <Checkbox id={id("follow")} checked={f.follow_up} onCheckedChange={(v) => setF({ ...f, follow_up: !!v })} className="mt-0.5 border-ink data-[state=checked]:bg-teal data-[state=checked]:border-teal" data-testid="download-follow-up-checkbox" />
              <label htmlFor={id("follow")} className="cursor-pointer text-[15px]">I'd like Michelle to follow up with me about support</label>
            </div>
            <Honeypot value={f.website} onChange={(v) => setF({ ...f, website: v })} />
            <button type="submit" disabled={busy} className="w-full inline-flex justify-center items-center gap-2 bg-teal text-paper rounded-full py-3.5 hover:bg-teal-deep transition-colors disabled:opacity-70" data-testid="download-submit-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}{busy ? "Preparing..." : "Download free"}
            </button>
            {status && <FormStatus status={status} testid="download-status" />}
            <p className="text-xs text-slate">Your email is only used by Deaf Education Consulting LLC and never shared.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
